import { BaseComponent } from '@ups/xplat/core';
import {
  Input,
  TemplateRef,
  Output,
  EventEmitter,
  Component,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';

@Component({
  template: '',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class ConfirmationDialogBaseComponent extends BaseComponent {
  public text = 'ConfirmationDialog';

  // WI 31903: signal-backed state so the dialog re-renders under OnPush + zoneless.
  // Written via @Input() setters (not signal input()) because `message` is also
  // set imperatively by open(), which a read-only signal input() forbids; keeping
  // all five on the setter pattern stays consistent and preserves the input API.
  readonly titleTextSignal = signal('Confirm?');
  @Input() set titleText(value: string) {
    this.titleTextSignal.set(value ?? 'Confirm?');
  }

  readonly cancelTextSignal = signal('Cancel');
  @Input() set cancelText(value: string) {
    this.cancelTextSignal.set(value ?? 'Cancel');
  }

  readonly confirmTextSignal = signal('Confirm');
  @Input() set confirmText(value: string) {
    this.confirmTextSignal.set(value ?? 'Confirm');
  }

  // Provide a simple string body or an ng-template ref.
  readonly messageSignal = signal<string | undefined>(undefined);
  @Input() set message(value: string) {
    this.messageSignal.set(value);
  }

  readonly bodyTemplateSignal = signal<TemplateRef<unknown> | undefined>(
    undefined
  ); // https://stackoverflow.com/questions/45976862/templateref-requires-type-argument
  @Input() set bodyTemplate(value: TemplateRef<unknown>) {
    this.bodyTemplateSignal.set(value);
  }

  // Output emitters are preferred when using template
  @Output() confirm = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  // callback functions useful when opening programmatically, but should be avoided when using through template.
  protected confirmCallback: () => void;
  protected cancelCallback: () => void;

  constructor() {
    super();
  }

  public open(
    message?: string,
    confirmCallback?: () => void,
    cancelCallback?: () => void
  ) {
    if (message) {
      this.message = message;
    }
    if (confirmCallback) {
      this.confirmCallback = confirmCallback;
    }
    if (cancelCallback) {
      this.cancelCallback = cancelCallback;
    }
  }

  onCancel() {
    if (this.cancelCallback) {
      this.cancelCallback();
    }
    this.cancelled.emit();
  }

  onConfirm() {
    if (this.confirmCallback) {
      this.confirmCallback();
    }
    this.confirm.emit();
  }
}
